<script setup lang="ts">
import { onMounted, watch } from 'vue';

import { useIntegrationStore } from '@/modules/integrations/store/useIntegrationStore';
import { useUserStore } from '@/store/useUserStore';
import { storeToRefs } from 'pinia';
import { paragon } from '@useparagon/connect';
import { Button } from '@/storybook';

const integrationStore = useIntegrationStore();
const { integration_token, integration_project_id } = storeToRefs(integrationStore);

const userStore = useUserStore();
const { isOrgUserAdmin } = storeToRefs(userStore);

onMounted(() => {
  integrationStore.fetchIntegrations();
});

watch(integration_token, newToken => {
  paragon.authenticate(integration_project_id.value ?? '', newToken ?? '');
});

const toggleIntegration = async (name: string) => {
  await paragon.authenticate(integration_project_id.value ?? '', integration_token.value ?? '');
  paragon.connect(name.toLowerCase(), { onInstall: handleSuccess, onUninstall: handleSuccess });
};

function handleSuccess() {
  integrationStore.fetchIntegrations(true);
}
</script>

<template>
  <div class="integrations-container">
    <div v-if="integrationStore.loading">Loading...</div>
    <ul class="cards-grid">
      <li v-for="integration in integrationStore.integrations" :key="integration.name" class="integration-card">
        <div class="integration-icon">
          <img :src="integration.icon" width="24" height="24" />
        </div>
        <div class="">
          <h2 class="integration-name">{{ integration.name }}</h2>
          <p class="integration-description">{{ integration.description }}</p>
          <div v-if="isOrgUserAdmin" class="integration-button">
            <Button
              :type="integration.enabled ? 'solid' : 'ghost'"
              :variant="integration.enabled ? 'green' : 'black'"
              @click.prevent="toggleIntegration(integration.name)"
            >
              {{ integration.enabled ? 'Manage' : 'Enable' }}
            </Button>
          </div>
          <div v-else class="integration-button">
            <div v-if="integration.enabled">
              <div class="flex items-center gap-2">
                <span data-v-2cc1431a="" data-testid="status-button-on" class="integration-status green">ON</span>
              </div>
            </div>
            <div v-else>
              <button data-testid="status-button-off" class="integration-status red">OFF</button>
              <span class="integration-status-desc">Contact your organization admin to enable {{ integration.name }}</span>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
<style scoped>
.integrations-container {
  @apply py-8;
}
.cards-grid {
  @apply grid gap-6 mt-4;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}
.integration-card {
  @apply flex items-start gap-6 p-8 bg-white dark:bg-zinc-800 rounded-10 border border-slate-300 dark:border-zinc-700;
}
.integration-icon {
  @apply w-14 h-14 flex items-center justify-center p-4 rounded-full bg-white border border-slate-300 dark:border-zinc-700;
}
.integration-name {
  @apply text-xl font-semibold text-slate-900 dark:text-zinc-300;
}
.integration-description {
  @apply text-sm text-slate-700 dark:text-zinc-400;
}
.integration-button {
  @apply mt-4;
}
.integration-status {
  @apply inline-flex items-center py-0.5 px-1 rounded-md text-sm font-semibold text-slate-900 dark:text-zinc-300 bg-slate-300 dark:bg-zinc-700;
}
.integration-status.green {
  @apply bg-green-500 dark:bg-green-500/50 text-white dark:text-green-300;
}
.integration-status.red {
  @apply bg-red-500 dark:bg-red-500/50 text-white dark:text-red-300;
}
.integration-status-desc {
  @apply py-0.5 px-1 rounded-md text-sm bg-slate-300 dark:bg-zinc-700 text-slate-700 dark:text-zinc-400;
}
</style>
